import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import HeroSection from './components/HeroSection';
import ProductCards from './components/ProductCards';
import HowItWorks from './components/HowItWorks';
import TrustSection from './components/TrustSection';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import SuccessPage from './pages/SuccessPage';
import IngredientsLibrary from './pages/IngredientsLibrary';
import IngredientDetail from './pages/IngredientDetail';
import LensesLibrary from './pages/LensesLibrary';
import LensDetail from './pages/LensDetail';
import AppPage from './pages/AppPage';
import ExtensionPage from './pages/ExtensionPage';
import MealPlannerPage from './pages/MealPlannerPage';
import ProductGuide from './pages/ProductGuide';
import ProductGuidesLibrary from './pages/ProductGuidesLibrary';
import ProductScan from './pages/ProductScan';
import { analytics, initializeAnalytics } from './firebase';
import { logEvent, setAnalyticsCollectionEnabled } from '@firebase/analytics';
import CookieConsent from './components/CookieConsent';

// Home Page
const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>FoodLens AI - Seed Oil Detector & Ingredient Scanner | Free</title>
        <meta name="description" content="Free seed oil detector and ingredient scanner for online grocery shopping. Instantly flags seed oils, preservatives, artificial colors, and 15+ concerns on Amazon, Walmart, and more. 100% ad-free." />
        <meta name="keywords" content="seed oil detector, food ingredient scanner, seed oil checker, food scanning, ingredient analysis, healthy eating, nutrition app" />
      </Helmet>
      <HeroSection />
      <ProductCards />
      <HowItWorks />
      <TrustSection />
    </>
  );
};

// Component to handle hash scrolling
const HashScrollHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return null;
};

function App() {
  useEffect(() => {
    initializeAnalytics();

    logEvent(analytics, 'page_view', {
      time_of_day: new Date().getHours(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screen_size: `${window.innerWidth}x${window.innerHeight}`
    });

    let lastScrollPosition = 0;
    const handleScroll = () => {
      const sections = ['products', 'how-it-works'];
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = (scrollPosition / documentHeight) * 100;

      if (scrollPercentage > lastScrollPosition + 25) {
        lastScrollPosition = Math.floor(scrollPercentage / 25) * 25;
        logEvent(analytics, 'scroll_depth', {
          depth_percentage: lastScrollPosition,
          time_on_page: Math.floor((Date.now() - window.performance.timing.navigationStart) / 1000)
        });
      }

      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
            logEvent(analytics, 'section_view', {
              section_id: sectionId,
              time_of_day: new Date().getHours()
            });
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCookieAccept = () => {
    initializeAnalytics();
    logEvent(analytics, 'page_view', {
      time_of_day: new Date().getHours(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screen_size: `${window.innerWidth}x${window.innerHeight}`
    });
  };

  const handleCookieDecline = () => {
    setAnalyticsCollectionEnabled(analytics, false);
  };

  return (
    <HelmetProvider>
      <BrowserRouter>
        <HashScrollHandler />
        <div className="min-h-screen bg-background flex flex-col">
          <Header />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/app" element={<AppPage />} />
              <Route path="/extension" element={<ExtensionPage />} />
              <Route path="/meal-planner" element={<MealPlannerPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/ingredients" element={<IngredientsLibrary />} />
              <Route path="/ingredients/:ingredientId" element={<IngredientDetail />} />
              <Route path="/lenses" element={<LensesLibrary />} />
              <Route path="/lenses/:lensId" element={<LensDetail />} />
              <Route path="/guides" element={<ProductGuidesLibrary />} />
              <Route path="/product/:barcode" element={<ProductScan />} />
              <Route path="/:lensSlug/:categorySlug" element={<ProductGuide />} />
              <Route path="/success" element={<SuccessPage />} />
            </Routes>
          </main>
          <Footer />
          <CookieConsent
            onAccept={handleCookieAccept}
            onDecline={handleCookieDecline}
          />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
